import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { financeService } from '../../services/financeService';
import DateRangePicker from '../Shared/DateRangePicker';
import HeaderNewTabButton from '../Shared/HeaderNewTabButton';
import { COMPACT_CURRENCY_COLUMN } from '../Shared/gridColumnPresets';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import '../Venues/VenuesPage.css';
import './FinancialsPage.css';
import './FinancialsReports.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const BOXES = {
  purchases: {
    title: 'Purchases',
    prefix: 'p',
    defaults: { rows: 'month', cols: 'entity', value: 'amount', cur: 'USD', basis: 'transaction' },
    values: [['amount', 'Amount'], ['quantity', 'Quantity (tickets)'], ['count', 'Count']],
  },
  orders: {
    title: 'Orders',
    prefix: 'o',
    defaults: { rows: 'month', cols: 'marketplace', value: 'amount', cur: 'USD', basis: 'transaction' },
    values: [['amount', 'Amount'], ['quantity', 'Quantity (tickets)'], ['count', 'Count'], ['avg_price', 'Avg ticket price']],
  },
};

const CURRENCY_OPTIONS = [
  ['USD', 'USD'], ['EUR', 'EUR'], ['GBP', 'GBP'], ['CHF', 'CHF'], ['PLN', 'PLN'],
  ['UNKNOWN', 'Unknown'], ['BY_CURRENCY', 'By currency'],
];
const AUTO_HEIGHT_MAX_ROWS = 20;

const todayStamp = () => new Date().toISOString().slice(0, 10).replace(/-/g, '');

const formatValue = (value, valueKind) => {
  if (value == null || value === '') return '-';
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  if (valueKind === 'count' || valueKind === 'quantity') return number.toLocaleString();
  return number.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Read one box's selections from the URL (prefix p_ / o_), falling back to its defaults.
const readBox = (searchParams, dataset) => {
  const { prefix, defaults } = BOXES[dataset];
  const read = (key) => searchParams.get(`${prefix}_${key}`);
  return {
    from: read('from') || '',
    to: read('to') || '',
    basis: read('basis') || defaults.basis,
    rows: read('rows') || defaults.rows,
    cols: read('cols') || defaults.cols,
    value: read('value') || defaults.value,
    cur: read('cur') || defaults.cur,
    includeVoid: read('void') === '1',
  };
};

const toApiParams = (dataset, box) => ({
  dataset,
  rows: box.rows,
  cols: box.cols,
  value: box.value,
  currency: box.cur,
  date_basis: box.basis,
  date_from: box.from || undefined,
  // Same as the Orders page: a start date alone means that single day.
  date_to: box.to || box.from || undefined,
  include_excluded: box.includeVoid || undefined,
});

const DRILL_COLUMNS = [
  { headerName: 'Type', field: 'row_type', width: 100 },
  { headerName: 'ID', field: 'id', width: 130 },
  { headerName: 'Date', field: 'date', width: 120 },
  { headerName: 'Event', field: 'event_name', minWidth: 200, flex: 1 },
  { headerName: 'Event Date', field: 'event_date', width: 120 },
  { headerName: 'Venue', field: 'venue', minWidth: 160 },
  { headerName: 'Region', field: 'region', width: 130 },
  { headerName: 'Entity', field: 'entity', width: 120, cellClass: (p) => (p.value === 'UNASSIGNED' ? 'finance-unassigned' : '') },
  { headerName: 'Marketplace', field: 'sale_point', width: 130 },
  { headerName: 'Platform', field: 'platform', width: 130 },
  { headerName: 'Card', field: 'card', width: 130 },
  { headerName: 'Paid by', field: 'cash_party', width: 110, cellClass: (p) => (p.value === 'UNASSIGNED' ? 'finance-unassigned' : '') },
  { headerName: 'Operator', field: 'operator', width: 100 },
  { headerName: 'Qty', field: 'quantity', width: 80, type: 'numericColumn' },
  { headerName: 'Amount', field: 'amount', width: 120, type: 'numericColumn', valueFormatter: (p) => formatValue(p.value, 'amount') },
  { headerName: 'Currency', field: 'currency', ...COMPACT_CURRENCY_COLUMN },
  { headerName: 'Status', field: 'status', width: 130 },
];

const PivotBlock = ({ block, result, rowsLabel, showCurrency, onCellOpen }) => {
  const gridRef = useRef(null);
  const valueKind = result.value;
  const colKeys = block.col_keys;

  const columnDefs = useMemo(() => [
    { headerName: rowsLabel, field: '__row', pinned: 'left', minWidth: 160, cellClass: 'finance-pivot-row-key' },
    ...colKeys.map((key, index) => ({
      headerName: key,
      field: `c${index}`,
      minWidth: 110,
      type: 'numericColumn',
      valueFormatter: (p) => formatValue(p.value, valueKind),
      cellClass: 'finance-pivot-cell',
    })),
    {
      headerName: 'Total',
      field: '__total',
      minWidth: 120,
      type: 'numericColumn',
      valueFormatter: (p) => formatValue(p.value, valueKind),
      cellClass: 'finance-pivot-cell finance-pivot-total',
    },
  ], [colKeys, rowsLabel, valueKind]);

  const { rowData, totalRow } = useMemo(() => {
    const cellMap = new Map(block.cells.map((cell) => [`${cell.row}\u0000${cell.col}`, cell.value]));
    const rows = block.row_keys.map((rowKey) => {
      const row = { __row: rowKey, __total: block.row_totals[rowKey] };
      colKeys.forEach((colKey, index) => {
        row[`c${index}`] = cellMap.get(`${rowKey}\u0000${colKey}`);
      });
      return row;
    });
    const total = { __row: 'Total', __total: block.grand_total };
    colKeys.forEach((colKey, index) => {
      total[`c${index}`] = block.col_totals[colKey];
    });
    return { rowData: rows, totalRow: [total] };
  }, [block, colKeys]);

  const handleCellClicked = (params) => {
    const field = params.colDef.field;
    if (field === '__row') return;
    const rowValue = params.node.rowPinned ? null : params.data.__row;
    const colValue = field === '__total' ? null : colKeys[Number(field.slice(1))];
    onCellOpen({ currency: block.currency, rowValue, colValue });
  };

  const exportCsv = () => {
    gridRef.current?.api?.exportDataAsCsv({
      fileName: `tikwiz-${result.dataset}-${result.rows}-by-${result.cols || 'none'}-${block.currency}-${todayStamp()}.csv`,
    });
  };

  const autoHeight = block.row_keys.length <= AUTO_HEIGHT_MAX_ROWS;

  return (
    <section className="finance-pivot-block">
      <div className="finance-section-head">
        <h3>{showCurrency ? block.currency : `${block.currency} · ${block.row_count} rows`}</h3>
        <button className="btn btn-secondary" type="button" onClick={exportCsv}>Export CSV</button>
      </div>
      <div className="venues-grid-shell">
        <div
          className={`ag-theme-quartz-dark venues-ag-grid finance-ag-grid ${autoHeight ? 'finance-ag-grid-auto' : ''}`}
        >
          <AgGridReact
            ref={gridRef}
            theme="legacy"
            rowData={rowData}
            pinnedBottomRowData={totalRow}
            columnDefs={columnDefs}
            defaultColDef={{ sortable: true, resizable: true }}
            domLayout={autoHeight ? 'autoHeight' : 'normal'}
            onCellClicked={handleCellClicked}
          />
        </div>
      </div>
    </section>
  );
};

const FinancialsReportsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routePrefix = location.pathname.startsWith('/dashboard') ? '/dashboard' : '/user';
  const [searchParams, setSearchParams] = useSearchParams();
  const [options, setOptions] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [drill, setDrill] = useState(null);
  const drillGridRef = useRef(null);
  const runDataset = searchParams.get('run');

  useEffect(() => {
    financeService.getReportOptions().then((response) => {
      if (response.success) setOptions(response);
      else setError(response.error);
    });
  }, []);

  const dimensionLabel = (dataset, key) => (
    options?.dimensions?.[dataset]?.find((dim) => dim.key === key)?.label || key
  );

  const updateBox = (dataset, key, value) => {
    const next = new URLSearchParams(searchParams);
    const param = `${BOXES[dataset].prefix}_${key}`;
    if (value === '' || value == null || value === false) next.delete(param);
    else next.set(param, value === true ? '1' : value);
    setSearchParams(next, { replace: true });
  };

  const runReport = async (dataset, params = searchParams) => {
    const box = readBox(params, dataset);
    setLoading(true);
    setError('');
    setDrill(null);
    const response = await financeService.getPivot(toApiParams(dataset, box));
    setLoading(false);
    if (!response.success) {
      setResult(null);
      setError(response.error);
      return;
    }
    setResult({ ...response, box });
  };

  // A shared / bookmarked URL with ?run=... reproduces the view on load.
  useEffect(() => {
    if (runDataset && BOXES[runDataset]) runReport(runDataset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRun = (dataset) => {
    const next = new URLSearchParams(searchParams);
    next.set('run', dataset);
    setSearchParams(next, { replace: true });
    runReport(dataset, next);
  };

  const openCell = async ({ currency, rowValue, colValue }) => {
    setDrill({ loading: true, currency, rowValue, colValue, items: [] });
    const response = await financeService.getPivotRows({
      ...toApiParams(result.dataset, result.box),
      value: undefined,
      row_value: rowValue ?? undefined,
      col_value: colValue ?? undefined,
      block_currency: currency,
    });
    setDrill({
      loading: false,
      currency,
      rowValue,
      colValue,
      items: response.success ? response.items : [],
      error: response.success ? '' : response.error,
    });
  };

  const renderBox = (dataset) => {
    const config = BOXES[dataset];
    const box = readBox(searchParams, dataset);
    const dims = options?.dimensions?.[dataset] || [];
    return (
      <div className="finance-report-box" key={dataset}>
        <h2>{config.title}</h2>
        <label className="finance-field">
          <span>Date</span>
          <div className="finance-date-row">
            <DateRangePicker
              id={`${dataset}-date-range`}
              value={{ start: box.from, end: box.to }}
              onChange={(range) => {
                const next = new URLSearchParams(searchParams);
                [['from', range?.start], ['to', range?.end]].forEach(([key, value]) => {
                  if (value) next.set(`${config.prefix}_${key}`, value);
                  else next.delete(`${config.prefix}_${key}`);
                });
                setSearchParams(next, { replace: true });
              }}
            />
            <select
              aria-label="Date basis"
              value={box.basis}
              onChange={(event) => updateBox(dataset, 'basis', event.target.value)}
            >
              <option value="transaction">Transaction date</option>
              <option value="event">Event date</option>
            </select>
          </div>
        </label>
        <label className="finance-field">
          <span>Rows (Y)</span>
          <select value={box.rows} onChange={(event) => updateBox(dataset, 'rows', event.target.value)}>
            {dims.map((dim) => <option key={dim.key} value={dim.key}>{dim.label}</option>)}
          </select>
        </label>
        <label className="finance-field">
          <span>Cols (X)</span>
          <select value={box.cols} onChange={(event) => updateBox(dataset, 'cols', event.target.value)}>
            <option value="none">None</option>
            {dims.filter((dim) => !dim.rows_only && dim.key !== box.rows).map((dim) => (
              <option key={dim.key} value={dim.key}>{dim.label}</option>
            ))}
          </select>
        </label>
        <label className="finance-field">
          <span>Value</span>
          <select value={box.value} onChange={(event) => updateBox(dataset, 'value', event.target.value)}>
            {config.values.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
        <label className="finance-field">
          <span>Currency</span>
          <select value={box.cur} onChange={(event) => updateBox(dataset, 'cur', event.target.value)}>
            {CURRENCY_OPTIONS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
        <div className="finance-box-actions">
          <label className="finance-checkbox">
            <input
              type="checkbox"
              checked={box.includeVoid}
              onChange={(event) => updateBox(dataset, 'void', event.target.checked)}
            />
            Include void/cancelled
          </label>
          <button
            className="btn btn-primary"
            type="button"
            disabled={loading || !options || box.rows === box.cols}
            onClick={() => handleRun(dataset)}
          >
            Run
          </button>
        </div>
      </div>
    );
  };

  const rowsLabel = result ? dimensionLabel(result.dataset, result.rows) : '';
  const colsLabel = result?.cols ? dimensionLabel(result.dataset, result.cols) : 'None';

  return (
    <div className="financials-page">
      <header className="financials-header">
        <div className="financials-header-content">
          <HeaderNewTabButton />
          <div>
            <h1>Reports</h1>
          </div>
          <div className="financials-header-actions">
            <button className="btn btn-secondary" type="button" onClick={() => navigate(`${routePrefix}/financials`)}>
              Back to Financials
            </button>
          </div>
        </div>
      </header>

      <main className="financials-main">
        {error && <div className="notification error">{error}</div>}

        <div className="finance-report-boxes">
          {renderBox('purchases')}
          {renderBox('orders')}
        </div>

        <section className="finance-prebuilt">
          <h2>Prebuilt reports</h2>
          <div className="finance-prebuilt-cards">
            <Link className="finance-prebuilt-card" to={`${routePrefix}/financials/reports/prepurchased-inventory`}>
              <strong>Prepurchased Inventory</strong>
              <span>Bought this year for events after year-end, sold vs unsold at the cutoff.</span>
            </Link>
            <Link className="finance-prebuilt-card" to={`${routePrefix}/financials/reports/entity-statement`}>
              <strong>Entity Year-End Statement</strong>
              <span>One legal entity, one year: trading result, year-end inventory, intercompany, data quality.</span>
            </Link>
          </div>
        </section>

        {loading && <div className="financials-loading"><p>Running report...</p></div>}

        {!loading && result && (
          <section className="finance-result">
            <div className="finance-section-head">
              <h2>
                {BOXES[result.dataset].title}
                {' · '}
                {rowsLabel}
                {' × '}
                {colsLabel}
              </h2>
            </div>
            {result.missing_date_count > 0 && (
              <div className="notification finance-warning">
                {result.missing_date_count} row(s) have no {result.box.basis === 'event' ? 'event' : 'transaction'} date
                and are not included in a date-filtered report. Clear the date range to see them under &quot;Missing date&quot;.
              </div>
            )}
            {result.blocks.length === 0 && <p className="finance-empty">No rows match these filters.</p>}
            {result.blocks.map((block) => (
              <PivotBlock
                key={block.currency}
                block={block}
                result={result}
                rowsLabel={rowsLabel}
                showCurrency={result.blocks.length > 1}
                onCellOpen={openCell}
              />
            ))}
            <p className="finance-hint">Click a cell to see the purchases / orders behind it.</p>
          </section>
        )}

        {drill && (
          <section className="finance-result">
            <div className="finance-section-head">
              <h3>
                {drill.currency}
                {' · '}
                {drill.rowValue ?? `All ${rowsLabel}`}
                {' × '}
                {drill.colValue ?? (result?.cols ? `All ${colsLabel}` : 'Total')}
                {!drill.loading && ` · ${drill.items.length} rows`}
              </h3>
              <div className="financials-header-actions">
                <button
                  className="btn btn-secondary"
                  type="button"
                  disabled={drill.loading || !drill.items.length}
                  onClick={() => drillGridRef.current?.api?.exportDataAsCsv({
                    fileName: `tikwiz-${result.dataset}-rows-${todayStamp()}.csv`,
                  })}
                >
                  Export CSV
                </button>
                <button className="btn btn-secondary" type="button" onClick={() => setDrill(null)}>Close</button>
              </div>
            </div>
            {drill.error && <div className="notification error">{drill.error}</div>}
            {drill.loading ? (
              <div className="financials-loading"><p>Loading rows...</p></div>
            ) : (
              <div className="venues-grid-shell">
                <div className="ag-theme-quartz-dark venues-ag-grid finance-ag-grid">
                  <AgGridReact
                    ref={drillGridRef}
                    theme="legacy"
                    rowData={drill.items}
                    columnDefs={DRILL_COLUMNS}
                    defaultColDef={{ sortable: true, filter: true, resizable: true }}
                    pagination
                    paginationPageSize={100}
                  />
                </div>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
};

export default FinancialsReportsPage;
